import { useCallback, useEffect, useMemo, useState } from 'react'
import { differenceInHours, format } from 'date-fns'
import { Link, useSearchParams } from 'react-router-dom'
import DatePicker from '../components/DatePicker'
import TimeslotSelector from '../components/TimeslotSelector'
import { getAvailableTimeslots, getBookingByToken, rescheduleBooking } from '../api/bookings'
import { useSettings } from '../hooks/useSettings'
import type { BookingDetails, TimeslotAvailability } from '../types/booking'

type ToastState = {
  tone: 'success' | 'error'
  message: string
}

const HOURS_RESCHEDULE_CUTOFF = 24
const MAX_RESCHEDULES = 1

const RescheduleBooking = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const { settings, loading: settingsLoading } = useSettings()

  const [loading, setLoading] = useState(true)
  const [booking, setBooking] = useState<BookingDetails | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [timeslots, setTimeslots] = useState<TimeslotAvailability[]>([])
  const [selectedTimeslotId, setSelectedTimeslotId] = useState<string>('')
  const [timeslotLoading, setTimeslotLoading] = useState(false)
  const [timeslotError, setTimeslotError] = useState<string | null>(null)

  const [isRescheduling, setIsRescheduling] = useState(false)
  const [toast, setToast] = useState<ToastState | null>(null)
  const [hasRescheduled, setHasRescheduled] = useState(false)
  const [eligibilityMessage, setEligibilityMessage] = useState<string | null>(null)

  const bookingStart = useMemo(() => {
    if (!booking?.startTime) return null
    const date = new Date(booking.startTime)
    return Number.isNaN(date.getTime()) ? null : date
  }, [booking?.startTime])

  const hoursUntilBooking = useMemo(() => {
    if (!bookingStart) return null
    return differenceInHours(bookingStart, new Date())
  }, [bookingStart])

  const withinCutoff = Boolean(hoursUntilBooking !== null && hoursUntilBooking < HOURS_RESCHEDULE_CUTOFF)
  const maxReschedulesReached = Boolean(booking?.rescheduleCount && booking.rescheduleCount >= MAX_RESCHEDULES)

  useEffect(() => {
    let isMounted = true

    const fetchBooking = async () => {
      if (!token) {
        setError('Booking not found. Please verify your link.')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await getBookingByToken(token)
        if (!isMounted) return

        setBooking(response.booking)
        const initialDate = new Date(response.booking.startTime)
        if (!Number.isNaN(initialDate.getTime())) {
          setSelectedDate(initialDate)
        }

        if (!response.canReschedule) {
          const message =
            response.booking.rescheduleCount >= MAX_RESCHEDULES
              ? 'Maximum reschedules reached.'
              : 'This reservation can no longer be rescheduled online.'
          setEligibilityMessage(message)
          if (response.booking.rescheduleCount >= MAX_RESCHEDULES) {
            setHasRescheduled(true)
          }
        } else {
          setEligibilityMessage(null)
        }

        if (response.booking.rescheduleCount >= MAX_RESCHEDULES) {
          setHasRescheduled(true)
        }
      } catch (err: unknown) {
        console.error(err)
        if (!isMounted) return
        setError(err instanceof Error ? err.message : 'Booking not found.')
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchBooking()

    return () => {
      isMounted = false
    }
  }, [token])

  const loadTimeslots = useCallback(
    async (date: Date | null) => {
      if (!date) return

      try {
        setTimeslotLoading(true)
        setTimeslotError(null)
        const formatted = format(date, 'yyyy-MM-dd')
        const available = await getAvailableTimeslots(formatted)
        setTimeslots(available)
        if (!available.some((slot) => slot.id === selectedTimeslotId)) {
          setSelectedTimeslotId('')
        }
      } catch (err) {
        console.error(err)
        setTimeslotError('We could not load available times for this date. Please try again.')
        setTimeslots([])
      } finally {
        setTimeslotLoading(false)
      }
    },
    [selectedTimeslotId],
  )

  useEffect(() => {
    if (selectedDate) {
      loadTimeslots(selectedDate)
    }
  }, [loadTimeslots, selectedDate])

  const eligibleToReschedule = !withinCutoff && !maxReschedulesReached && !hasRescheduled && !eligibilityMessage

  const handleDateChange = (nextDate: Date) => {
    setSelectedDate(nextDate)
    setSelectedTimeslotId('')
  }

  const handleReschedule = async () => {
    if (!booking || !token || !selectedTimeslotId) return

    try {
      setIsRescheduling(true)
      setToast(null)
      const response = await rescheduleBooking(booking.id, token, selectedTimeslotId)
      setToast({
        tone: 'success',
        message: response.message ?? 'Your reservation has been updated. We look forward to seeing you.',
      })
      setHasRescheduled(true)
      if (response.new_booking_details) {
        setBooking(response.new_booking_details)
      }
    } catch (err: unknown) {
      console.error(err)
      setToast({
        tone: 'error',
        message:
          err instanceof Error ? err.message : 'We could not update your reservation right now. Please try again.',
      })
    } finally {
      setIsRescheduling(false)
    }
  }

  const renderContent = () => {
    if (loading || settingsLoading) {
      return (
        <div className="space-y-6">
          <div className="h-4 w-48 animate-pulse rounded-full bg-widget-primary/20" />
          <div className="h-24 animate-pulse rounded-3xl bg-widget-primary/10" />
          <div className="h-[420px] animate-pulse rounded-3xl bg-widget-primary/10" />
        </div>
      )
    }

    if (error) {
      return (
        <div role="alert" className="superz-widget-panel p-6 text-center text-sm text-red-200">
          {error}
        </div>
      )
    }

    if (!booking) {
      return (
        <div role="alert" className="superz-widget-panel p-6 text-center text-sm text-red-200">
          Booking not found.
        </div>
      )
    }

    return (
      <>
        <section className="superz-widget-panel space-y-4 p-6">
          <header className="space-y-2">
            <p className="text-xs uppercase tracking-[0.3em] text-widget-accent/70">Booking Overview</p>
            <h1 className="font-display text-3xl text-widget-accent">Reschedule your experience</h1>
          </header>

          <dl className="grid gap-4 text-sm sm:grid-cols-2">
            <div className="rounded-2xl border border-widget-primary/30 bg-widget-primary/10 p-4">
              <dt className="text-xs uppercase tracking-[0.3em] text-widget-accent/60">Guest</dt>
              <dd className="mt-1 text-lg font-semibold text-widget-accent">{booking.customerName}</dd>
              <dd className="text-xs text-widget-accent/60">{booking.customerEmail ?? 'Email on file'}</dd>
            </div>
            <div className="rounded-2xl border border-widget-primary/30 bg-widget-primary/10 p-4">
              <dt className="text-xs uppercase tracking-[0.3em] text-widget-accent/60">Current reservation</dt>
              <dd className="mt-1 text-lg font-semibold text-widget-accent">
                {bookingStart ? format(bookingStart, 'EEEE, MMMM do') : 'Date unavailable'}
              </dd>
              <dd className="text-xs text-widget-accent/60">
                {bookingStart ? format(bookingStart, 'HH:mm') : 'Time unavailable'} ·{' '}
                {booking.guestCount} guest{booking.guestCount !== 1 && 's'}
              </dd>
            </div>
          </dl>

          <div
            className={`rounded-3xl border px-6 py-5 text-sm ${
              withinCutoff
                ? 'border-red-500/30 bg-red-500/10 text-red-100'
                : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'
            }`}
          >
            {withinCutoff ? (
              <>
                <p className="font-semibold">Too late to reschedule</p>
                <p className="mt-1 text-xs opacity-80">
                  We are within 24 hours of your reservation. Online rescheduling is no longer available.
                </p>
              </>
            ) : (
              <>
                <p className="font-semibold">You may reschedule once</p>
                <p className="mt-1 text-xs opacity-80">
                  Select a new date and time below. Reschedules are limited to one change per booking.
                </p>
              </>
            )}
          </div>
        </section>

        <section className="space-y-6">
          <DatePicker
            selectedDate={selectedDate}
            onChange={handleDateChange}
            closedDates={settings.closedDates}
            openingHours={settings.openingHours}
            minDate={new Date()}
          />

          <TimeslotSelector
            selectedDate={selectedDate}
            timeslots={timeslots}
            selectedTimeslotId={selectedTimeslotId || null}
            onSelect={setSelectedTimeslotId}
            loading={timeslotLoading}
            error={timeslotError ?? undefined}
            cutoffMinutes={HOURS_RESCHEDULE_CUTOFF * 60}
          />
        </section>

        <section className="superz-widget-panel space-y-6 p-6">
          {eligibilityMessage && (
            <p role="alert" className="rounded-2xl border border-widget-primary/30 bg-widget-primary/10 p-4 text-sm text-widget-accent/80">
              {eligibilityMessage}
            </p>
          )}

          {maxReschedulesReached && (
            <p className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-100">
              Maximum reschedules reached.
            </p>
          )}

          <div className="space-y-3 text-sm text-widget-accent/80">
            <p>
              Rescheduling is complimentary for our VIP guests. Your confirmation email will reflect the new date once the
              change is accepted.
            </p>
            <p>
              Prefer to cancel instead?{' '}
              <Link
                to={`/cancel?token=${encodeURIComponent(token)}`}
                className="underline decoration-widget-secondary decoration-2 underline-offset-4 hover:text-widget-secondary"
              >
                Cancel your reservation
              </Link>
              .
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={handleReschedule}
              disabled={
                isRescheduling ||
                hasRescheduled ||
                withinCutoff ||
                !eligibleToReschedule ||
                !selectedTimeslotId ||
                Boolean(timeslotError)
              }
              className="inline-flex items-center justify-center rounded-full border border-widget-secondary bg-widget-secondary px-8 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-widget-primary transition-all duration-200 shadow-[0_4px_12px_rgba(var(--widget-secondary-rgb),0.3)] hover:shadow-[0_8px_20px_rgba(var(--widget-secondary-rgb),0.4)] hover:scale-102 active:scale-98 focus:outline-none focus-visible:ring-2 focus-visible:ring-widget-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-widget-primary disabled:cursor-not-allowed disabled:border-widget-accent/20 disabled:bg-widget-accent/10 disabled:text-widget-accent/40 disabled:shadow-none"
              aria-disabled={
                isRescheduling ||
                hasRescheduled ||
                withinCutoff ||
                !eligibleToReschedule ||
                !selectedTimeslotId ||
                Boolean(timeslotError)
              }
            >
              {isRescheduling ? 'Rescheduling…' : hasRescheduled ? 'Rescheduled' : 'Confirm Reschedule'}
            </button>

            <p className="text-xs uppercase tracking-[0.3em] text-widget-accent/50">
              One complimentary reschedule per booking
            </p>
          </div>
        </section>
      </>
    )
  }

  return (
    <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-10 text-widget-accent">
      {toast && (
        <div
          role="status"
          aria-live="assertive"
          className={`sticky top-6 z-20 rounded-full px-5 py-3 text-sm shadow-lg backdrop-blur ${
            toast.tone === 'success' ? 'bg-emerald-500/20 text-emerald-100' : 'bg-red-500/20 text-red-100'
          }`}
        >
          {toast.message}
        </div>
      )}
      {renderContent()}
    </main>
  )
}

export default RescheduleBooking


